import { useRef } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import theme from "../../style/theme";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { OutlinedBlackButton } from "../button/OutlinedBlackButton";
import CloseSmallLineIcon from "../icons/CloseSmallLineIcon/CloseSmallLineIcon";
import File3LineIcon from "../icons/File3LineIcon/File3LineIcon";
import FileUploadLineIcon from "../icons/FileUploadLineIcon/FileUploadLineIcon";
import LinearProgressBar from "../progressbar/linear-progress-bar";

interface Props {
    progress: number;
    checkedExcelData: any[];
    setFiles: (files: File[]) => void;
    setRemoveFiles: (index: number) => void;
}

export const UserExcelFileDropZone: React.FC<Props> = ({
    progress,
    checkedExcelData,
    setFiles,
    setRemoveFiles,
}) => {
    const InputRef = useRef<HTMLInputElement>(null);
    const { acceptedFiles, getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
    });

    useDidMountEffect(() => {
        setFiles(acceptedFiles);
        if (InputRef && InputRef.current && InputRef.current.value) {
            InputRef.current.value = "";
        }
    }, [acceptedFiles]);

    return (
        <UserExcelFileDropZoneContainer {...getRootProps({ className: "dropzone" })}>
            {progress > 0 && progress < 100 ? (
                <>
                    <FileUploadLineIconContainer>
                        <FileUploadLineIcon
                            width="105px"
                            height="105px"
                            fill={theme.palette.primitives.gray300}
                        />
                    </FileUploadLineIconContainer>
                    <LoadingContainer>
                        <LoadingPlaceHolderContainer>
                            파일 업로드중...
                        </LoadingPlaceHolderContainer>
                        <LinearProgressBar progress={progress} />
                        <LoadingFileNameContainer>
                            {acceptedFiles.map((acceptedFile) => (
                                <LoadingFileNameField>
                                    파일명 : {acceptedFile.name}
                                </LoadingFileNameField>
                            ))}
                        </LoadingFileNameContainer>
                    </LoadingContainer>
                </>
            ) : (
                <>
                    {checkedExcelData.length === 0 ? (
                        <>
                            <FileUploadLineIconContainer>
                                <FileUploadLineIcon
                                    width="105px"
                                    height="105px"
                                    fill={theme.palette.primitives.gray300}
                                />
                            </FileUploadLineIconContainer>
                            <OutlinedBlackButtonContainer>
                                <OutlinedBlackButton
                                    width="221px"
                                    height="51px"
                                    text="파일 불러오기"
                                    onClick={open}
                                    sx={{
                                        fontWeight: "400",
                                        fontSize: "16px",
                                        fontHeight: "19px",
                                        color: "black",
                                    }}
                                />
                            </OutlinedBlackButtonContainer>
                            <PlaceHolderContainer>
                                또는 파일을 드래그해서 놓으세요
                            </PlaceHolderContainer>
                        </>
                    ) : (
                        <>
                            <CheckedExcelFileContainer>
                                <CheckedExcelFileListContainer>
                                    {checkedExcelData.map((data, index) => (
                                        <>
                                            <CheckedExcelFileItemContainer>
                                                <CheckedSpaceBetweenLeftContainer>
                                                    <File3LineIcon />
                                                    <CheckedExcelFileItemField>
                                                        {data.name}
                                                    </CheckedExcelFileItemField>
                                                </CheckedSpaceBetweenLeftContainer>
                                                <CheckedSpaceBetweenRightContainer
                                                    onClick={() => setRemoveFiles(index)}
                                                >
                                                    <CloseSmallLineIcon
                                                        width="21px"
                                                        height="21px"
                                                        fill={theme.palette.primitives.gray300}
                                                    />
                                                </CheckedSpaceBetweenRightContainer>
                                            </CheckedExcelFileItemContainer>
                                            <Divider />
                                        </>
                                    ))}
                                </CheckedExcelFileListContainer>
                            </CheckedExcelFileContainer>
                        </>
                    )}
                </>
            )}
        </UserExcelFileDropZoneContainer>
    );
};

const UserExcelFileDropZoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 388px;
  height: 508px;
  border: 1px solid ${theme.palette.primitives.gray200};
  border-radius: 8px;
`;

const FileUploadLineIconContainer = styled.div`
  margin-top: 151px;
`;

const OutlinedBlackButtonContainer = styled.div`
  margin-top: 24px;
`;

const PlaceHolderContainer = styled.div`
  margin-top: 16px;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: ${theme.palette.primitives.gray500};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
`;

const LoadingPlaceHolderContainer = styled.div`
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: ${theme.palette.primitives.gray400};
`;

const LoadingFileNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 32px;
`;

const LoadingFileNameField = styled.div`
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.black};
`;

const CheckedExcelFileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 151px;
`;

const CheckedExcelFileListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CheckedExcelFileItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 8px 20px 6px 24px;
`;

const CheckedExcelFileItemField = styled.div`
  margin-left: 5px;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.black};
  margin-top: 2px;
`;

const CheckedSpaceBetweenLeftContainer = styled.div`
  display: flex;
  align-items: start;
  word-break: break-all;
`;

const CheckedSpaceBetweenRightContainer = styled.div`
  cursor: pointer;
`;

const Divider = styled.div`
  margin: 0 20px;
  height: 1px;
  background: ${theme.palette.primitives.gray200};
`; 